// START_MODULE_CONTRACT
//   PURPOSE: Merge фактических часов с плановым расписанием и пересчёт spans (UC-016, UC-021).
//   SCOPE: index actuals, reserve role day capacity, recalculate task spans, sum effective hours.
//   DEPENDS: timelineUtils (computeWorkloadSchedule, buildStages, getRoleDayCapacity, isRoleAbsentOn)
//   LINKS: M-FE-ACTUAL-SCHEDULE, V-M-FE-ACTUAL-SCHEDULE
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   actualHoursKey - stable key for feature+role+date
//   indexActualHours - Map key builder
//   buildRoleReservedHours - Map roleId → (iso → часы, зарезервированные фактом) (UC-021)
//   applyActualsToWorkloadSchedule - main merge engine
//   sumEffectiveHours - row total helper
// END_MODULE_MAP
//
// START_CHANGE_SUMMARY
//   LAST_CHANGE: 2026-07-28 - UC-021: plannedHoursByDate отдаёт плановые часы после учёта резервов — диалог факта подставляет значение из ячейки, а не план до сдвига.
//   LAST_CHANGE: 2026-07-28 - UC-021: факт резервирует ёмкость своего дня; плановый floor роли двигают только плановые дни, поэтому факт не сдвигает следующую задачу.
//   LAST_CHANGE: 2026-07-15 - fix: после факта < плана последующие дни берут полную net-ёмкость, не хвост планового span (UC-016).
// END_CHANGE_SUMMARY

import { addDays, startOfDay, toIsoDate } from '../../utils/dateUtils';
import {
  buildStages,
  computeWorkloadSchedule,
  getRoleDayCapacity,
  isFeatureOnTimeline,
  isRoleAbsentOn,
} from './timelineUtils';

const HOURS_EPSILON = 0.001;
const SCHEDULE_GUARD_DAYS = 5000;

// START_METHOD_actualHoursKey
// START_CONTRACT: actualHoursKey
//   PURPOSE: Стабильный ключ feature + role + ISO date.
//   INPUTS: featureId, projectRoleId, iso.
//   OUTPUTS: string key.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-ACTUAL-SCHEDULE
// END_CONTRACT: actualHoursKey
export function actualHoursKey(featureId, projectRoleId, iso) {
  return `${featureId}__${projectRoleId}__${iso}`;
}
// END_METHOD_actualHoursKey

// START_METHOD_toActualIso
// START_CONTRACT: toActualIso
//   PURPOSE: Приводит workDate записи факта к ISO-строке.
//   INPUTS: workDate string|Date.
//   OUTPUTS: string ISO date.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-ACTUAL-SCHEDULE
// END_CONTRACT: toActualIso
function toActualIso(workDate) {
  return typeof workDate === 'string' ? workDate : toIsoDate(workDate);
}
// END_METHOD_toActualIso

// START_METHOD_indexActualHours
// START_CONTRACT: indexActualHours
//   PURPOSE: Индексирует записи API в Map по ключу дня.
//   INPUTS: actualHoursList array.
//   OUTPUTS: Map<string, number>.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-ACTUAL-SCHEDULE
// END_CONTRACT: indexActualHours
export function indexActualHours(actualHoursList) {
  const map = new Map();
  (actualHoursList ?? []).forEach((item) => {
    map.set(
      actualHoursKey(item.featureId, item.projectRoleId, toActualIso(item.workDate)),
      item.actualHours,
    );
  });
  return map;
}
// END_METHOD_indexActualHours

// START_METHOD_buildRoleReservedHours
// START_CONTRACT: buildRoleReservedHours
//   PURPOSE: Суммирует фактические часы роли по дням — зарезервированная ёмкость дня (UC-021).
//   INPUTS: actualHoursList array, featureIds Set|null — учитывать только эти фичи.
//   OUTPUTS: Map<roleId, Map<iso, hours>>.
//   SIDE_EFFECTS: none.
//   LINKS: V-M-FE-ACTUAL-SCHEDULE, UC-021
// END_CONTRACT: buildRoleReservedHours
export function buildRoleReservedHours(actualHoursList, featureIds = null) {
  const reserved = new Map();
  (actualHoursList ?? []).forEach((item) => {
    if (featureIds && !featureIds.has(item.featureId)) {
      return;
    }
    const hours = Number(item.actualHours);
    if (!Number.isFinite(hours) || hours <= 0) {
      return;
    }
    addReservedHours(reserved, item.projectRoleId, toActualIso(item.workDate), hours);
  });
  return reserved;
}
// END_METHOD_buildRoleReservedHours

// START_METHOD_addReservedHours
// START_CONTRACT: addReservedHours
//   PURPOSE: Добавляет зарезервированные часы роли на ISO-день.
//   INPUTS: reserved Map, roleId, iso, hours.
//   OUTPUTS: void (mutates reserved).
//   SIDE_EFFECTS: updates reserved map.
//   LINKS: UC-021
// END_CONTRACT: addReservedHours
function addReservedHours(reserved, roleId, iso, hours) {
  let byDate = reserved.get(roleId);
  if (!byDate) {
    byDate = new Map();
    reserved.set(roleId, byDate);
  }
  byDate.set(iso, (byDate.get(iso) ?? 0) + hours);
}
// END_METHOD_addReservedHours

// START_METHOD_getReservedHours
// START_CONTRACT: getReservedHours
//   PURPOSE: Зарезервированные часы роли на ISO-день.
//   INPUTS: reserved Map, roleId, iso.
//   OUTPUTS: number.
//   SIDE_EFFECTS: none.
//   LINKS: UC-021
// END_CONTRACT: getReservedHours
function getReservedHours(reserved, roleId, iso) {
  return reserved.get(roleId)?.get(iso) ?? 0;
}
// END_METHOD_getReservedHours

// START_METHOD_sumEffectiveHours
// START_CONTRACT: sumEffectiveHours
//   PURPOSE: Суммирует часы по дням из Map.
//   INPUTS: hoursByDate Map.
//   OUTPUTS: number.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-ACTUAL-SCHEDULE
// END_CONTRACT: sumEffectiveHours
export function sumEffectiveHours(hoursByDate) {
  if (!hoursByDate) {
    return 0;
  }
  let sum = 0;
  hoursByDate.forEach((hours) => {
    sum += hours;
  });
  return sum;
}
// END_METHOD_sumEffectiveHours

// START_METHOD_parseIsoDate
// START_CONTRACT: parseIsoDate
//   PURPOSE: Парсит 'YYYY-MM-DD' в локальную Date.
//   INPUTS: iso string.
//   OUTPUTS: Date.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-ACTUAL-SCHEDULE
// END_CONTRACT: parseIsoDate
function parseIsoDate(iso) {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
}
// END_METHOD_parseIsoDate

// START_METHOD_nextIso
// START_CONTRACT: nextIso
//   PURPOSE: Следующий календарный день в ISO-формате.
//   INPUTS: iso string.
//   OUTPUTS: string ISO date.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-ACTUAL-SCHEDULE
// END_CONTRACT: nextIso
function nextIso(iso) {
  return toIsoDate(addDays(parseIsoDate(iso), 1));
}
// END_METHOD_nextIso

// START_METHOD_maxIso
// START_CONTRACT: maxIso
//   PURPOSE: Возвращает позднюю из двух ISO-дат, корректно обрабатывая null.
//   INPUTS: a string|null, b string|null.
//   OUTPUTS: string|null.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-ACTUAL-SCHEDULE
// END_CONTRACT: maxIso
function maxIso(a, b) {
  if (!a) return b ?? null;
  if (!b) return a;
  return a >= b ? a : b;
}
// END_METHOD_maxIso

// START_METHOD_minIso
// START_CONTRACT: minIso
//   PURPOSE: Возвращает раннюю из двух ISO-дат, корректно обрабатывая null.
//   INPUTS: a string|null, b string|null.
//   OUTPUTS: string|null.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-ACTUAL-SCHEDULE
// END_CONTRACT: minIso
function minIso(a, b) {
  if (!a) return b ?? null;
  if (!b) return a;
  return a <= b ? a : b;
}
// END_METHOD_minIso

// START_METHOD_collectTaskActuals
// START_CONTRACT: collectTaskActuals
//   PURPOSE: Выбирает факты одной задачи (feature + role) из общего индекса.
//   INPUTS: actualMap Map, featureId, roleId.
//   OUTPUTS: Map<iso, hours>.
//   SIDE_EFFECTS: none.
//   LINKS: M-FE-ACTUAL-SCHEDULE
// END_CONTRACT: collectTaskActuals
function collectTaskActuals(actualMap, featureId, roleId) {
  const prefix = `${featureId}__${roleId}__`;
  const byDate = new Map();
  actualMap.forEach((hours, key) => {
    if (key.startsWith(prefix)) {
      byDate.set(key.slice(prefix.length), hours);
    }
  });
  return byDate;
}
// END_METHOD_collectTaskActuals

// START_METHOD_recalculateTaskSpan
// START_CONTRACT: recalculateTaskSpan
//   PURPOSE: Один хронологический проход по дням задачи: дни с фактом берут факт, остальные —
//            плановые часы в пределах свободной ёмкости дня роли (UC-016, UC-021).
//   INPUTS: { featureId, roleId, estimate, plannedSpan, taskActuals, floorIso, workloadPercent,
//            overheads, absences, workingHoursByDate, reservedByRole }.
//   OUTPUTS: { span: { startIso, endIso, hoursByDate, plannedHoursByDate },
//              plannedIsoList: string[], plannedEndIso: string|null } | null;
//            plannedHoursByDate — плановые часы ПОСЛЕ учёта резервов (значение из ячейки),
//            для дней с сохранённым фактом — исходный план этого дня.
//   SIDE_EFFECTS: console.debug branch markers BLOCK_TASK_FULLY_ACTUAL / BLOCK_PLAN_REDUCED_BY_RESERVED /
//                 BLOCK_PLAN_KEPT / BLOCK_FLOOR_FROM_PLANNED_END.
//   PRECONDITIONS: floorIso — ISO-дата, раньше которой плановые часы задачи не размещаются.
//   POSTCONDITIONS: плановые часы дня не превышают (net-ёмкость − зарезервированные часы роли).
//   INVARIANTS: дни только с фактом не участвуют в расчёте plannedEndIso.
//   LINKS: V-M-FE-ACTUAL-SCHEDULE, UC-016, UC-021
// END_CONTRACT: recalculateTaskSpan
function recalculateTaskSpan({
  featureId,
  roleId,
  estimate,
  plannedSpan,
  taskActuals,
  floorIso,
  workloadPercent,
  overheads,
  absences,
  workingHoursByDate,
  reservedByRole,
}) {
  if (!(estimate > 0)) {
    return null;
  }

  // BUG_FIX_CONTEXT: Previous approach re-ran the planned flow-shop engine over effective spans, so a
  // stored actual after the planned end pushed the role pipeline cursor and shifted the next task
  // (UC-021). This fix keeps actual hours as a capacity reservation of their own day and advances the
  // role floor by planned days only, so the next task keeps its planned start and only loses the hours
  // reserved on that day.

  const actualIsoList = [...taskActuals.keys()].sort();
  const lastActualIso = actualIsoList.length > 0 ? actualIsoList[actualIsoList.length - 1] : null;

  const effective = new Map();
  const plannedIsoList = [];
  let remaining = estimate;
  let reducedDayCount = 0;
  let cursorIso = minIso(floorIso, actualIsoList[0] ?? null) ?? floorIso;
  let guard = 0;

  while (guard < SCHEDULE_GUARD_DAYS) {
    guard += 1;
    if (remaining <= HOURS_EPSILON && (!lastActualIso || cursorIso > lastActualIso)) {
      break;
    }

    const storedActual = taskActuals.get(cursorIso);
    if (storedActual !== undefined) {
      // START_BLOCK_ACTUAL_DAY
      effective.set(cursorIso, storedActual);
      if (remaining > HOURS_EPSILON) {
        remaining -= Math.min(storedActual, remaining);
      }
      // END_BLOCK_ACTUAL_DAY
    } else if (remaining > HOURS_EPSILON && cursorIso >= floorIso) {
      // START_BLOCK_PLAN_DAY
      const capacity = getRoleDayCapacity(
        cursorIso,
        roleId,
        workloadPercent,
        overheads,
        workingHoursByDate,
      );
      if (capacity > 0 && !isRoleAbsentOn(cursorIso, absences)) {
        const reserved = getReservedHours(reservedByRole, roleId, cursorIso);
        const available = capacity - reserved;
        if (available > HOURS_EPSILON) {
          const dayHours = Math.min(remaining, available);
          effective.set(cursorIso, dayHours);
          plannedIsoList.push(cursorIso);
          remaining -= dayHours;
          if (reserved > HOURS_EPSILON) {
            reducedDayCount += 1;
          }
        }
      }
      // END_BLOCK_PLAN_DAY
    }

    cursorIso = nextIso(cursorIso);
  }

  if (effective.size === 0) {
    return null;
  }

  const sortedEffective = [...effective.keys()].sort();
  const plannedEndIso = plannedIsoList.length > 0
    ? plannedIsoList[plannedIsoList.length - 1]
    : null;

  // START_BLOCK_EFFECTIVE_PLANNED_HOURS
  // Плановые часы после учёта резервов: именно они видны в ячейке, поэтому диалог
  // редактирования факта должен подставлять их, а не исходный план до сдвига (UC-021).
  // Для дней с сохранённым фактом плановой величины в этом проходе нет — берём исходный план.
  const plannedIsoSet = new Set(plannedIsoList);
  const originalPlannedHours = plannedSpan?.hoursByDate ?? null;
  const plannedHoursByDate = new Map();
  sortedEffective.forEach((iso) => {
    if (plannedIsoSet.has(iso)) {
      plannedHoursByDate.set(iso, effective.get(iso));
    } else if (originalPlannedHours?.has(iso)) {
      plannedHoursByDate.set(iso, originalPlannedHours.get(iso));
    }
  });
  // END_BLOCK_EFFECTIVE_PLANNED_HOURS

  logTaskOutcome({
    featureId,
    roleId,
    plannedIsoList,
    plannedEndIso,
    reducedDayCount,
    lastActualIso,
    floorIso,
  });

  return {
    span: {
      startIso: sortedEffective[0],
      endIso: sortedEffective[sortedEffective.length - 1],
      hoursByDate: effective,
      plannedHoursByDate,
    },
    plannedIsoList,
    plannedEndIso,
  };
}
// END_METHOD_recalculateTaskSpan

// START_METHOD_logTaskOutcome
// START_CONTRACT: logTaskOutcome
//   PURPOSE: Пишет взаимоисключающий маркер исхода задачи и отдельный маркер факта вне плана (UC-021).
//   INPUTS: { featureId, roleId, plannedIsoList, plannedEndIso, reducedDayCount, lastActualIso, floorIso }.
//   OUTPUTS: void.
//   SIDE_EFFECTS: console.debug markers.
//   LINKS: V-M-FE-ACTUAL-SCHEDULE, UC-021
// END_CONTRACT: logTaskOutcome
function logTaskOutcome({
  featureId,
  roleId,
  plannedIsoList,
  plannedEndIso,
  reducedDayCount,
  lastActualIso,
  floorIso,
}) {
  if (typeof console === 'undefined') {
    return;
  }
  const context = { featureId, roleId, plannedEndIso };

  if (plannedIsoList.length === 0) {
    console.debug('[actualScheduleUtils][recalculateTaskSpan][BLOCK_TASK_FULLY_ACTUAL]', context);
  } else if (reducedDayCount > 0) {
    console.debug('[actualScheduleUtils][recalculateTaskSpan][BLOCK_PLAN_REDUCED_BY_RESERVED]', {
      ...context,
      reducedDayCount,
    });
  } else {
    console.debug('[actualScheduleUtils][recalculateTaskSpan][BLOCK_PLAN_KEPT]', context);
  }

  if (lastActualIso && lastActualIso > (plannedEndIso ?? floorIso)) {
    console.debug('[actualScheduleUtils][recalculateTaskSpan][BLOCK_FLOOR_FROM_PLANNED_END]', {
      ...context,
      lastActualIso,
    });
  }
}
// END_METHOD_logTaskOutcome

// START_METHOD_applyActualsToWorkloadSchedule
// START_CONTRACT: applyActualsToWorkloadSchedule
//   PURPOSE: Применяет фактические часы к плановому flow-shop расписанию (UC-016, UC-021).
//   INPUTS: features, employees, projectRoles, workingHoursByDate, overheads, actualHoursList.
//   OUTPUTS: { taskSpans, featureSpans }.
//   SIDE_EFFECTS: console debug markers.
//   INVARIANTS: плановый старт следующей задачи роли определяется плановыми днями предыдущей,
//               а не днями её факта (UC-021).
//   LINKS: V-M-FE-ACTUAL-SCHEDULE, UC-016, UC-021
// END_CONTRACT: applyActualsToWorkloadSchedule
export function applyActualsToWorkloadSchedule(
  features,
  employees,
  projectRoles,
  workingHoursByDate,
  overheads = [],
  actualHoursList = [],
) {
  const planned = computeWorkloadSchedule(
    features,
    employees,
    projectRoles,
    workingHoursByDate,
    overheads,
  );

  const actualMap = indexActualHours(actualHoursList);
  if (actualMap.size === 0) {
    if (typeof console !== 'undefined') {
      console.debug('[actualScheduleUtils][applyActualsToWorkloadSchedule][BLOCK_ACTUAL_PLAN_ONLY]');
    }
    return planned;
  }

  if (typeof console !== 'undefined') {
    console.debug('[actualScheduleUtils][applyActualsToWorkloadSchedule][BLOCK_ACTUAL_APPLIED]');
  }

  const roleById = new Map((projectRoles ?? []).map((role) => [role.id, role]));
  const employeeByRoleId = new Map(
    (employees ?? []).map((employee) => [employee.projectRoleId, employee]),
  );

  const scheduled = (features ?? [])
    .filter((feature) => isFeatureOnTimeline(feature))
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // START_BLOCK_RESERVE_ACTUAL_CAPACITY
  const reservedByRole = buildRoleReservedHours(
    actualHoursList,
    new Set(scheduled.map((feature) => feature.id)),
  );
  // END_BLOCK_RESERVE_ACTUAL_CAPACITY

  const fallbackFloorIso = planned.taskSpans.size > 0
    ? [...planned.taskSpans.values()][0].startIso
    : toIsoDate(startOfDay(new Date()));

  const effectiveTaskSpans = new Map();
  const rolePlannedFloorIso = new Map();

  scheduled.forEach((feature) => {
    const featureFloorIso = feature.startDate ?? fallbackFloorIso;

    const roleIds = feature.estimates
      .filter((estimate) => estimate.estimate > 0)
      .map((estimate) => estimate.projectRoleId)
      .sort((a, b) => (roleById.get(a)?.sortOrder ?? 0) - (roleById.get(b)?.sortOrder ?? 0));

    const stages = buildStages(roleIds, roleById);
    let stagePrevPlannedEndIso = null;

    stages.forEach((stage) => {
      const stageFloorIso = stagePrevPlannedEndIso
        ? nextIso(stagePrevPlannedEndIso)
        : featureFloorIso;
      let stagePlannedEndIso = null;

      stage.forEach((roleId) => {
        const estimate = feature.estimates.find((item) => item.projectRoleId === roleId);
        const role = roleById.get(roleId);
        const key = `${feature.id}__${roleId}`;

        const result = recalculateTaskSpan({
          featureId: feature.id,
          roleId,
          estimate: estimate.estimate,
          plannedSpan: planned.taskSpans.get(key) ?? null,
          taskActuals: collectTaskActuals(actualMap, feature.id, roleId),
          floorIso: maxIso(stageFloorIso, rolePlannedFloorIso.get(roleId) ?? null),
          workloadPercent: role?.workloadPercent ?? 100,
          overheads,
          absences: employeeByRoleId.get(roleId)?.absences ?? [],
          workingHoursByDate,
          reservedByRole,
        });

        if (!result) {
          return;
        }

        effectiveTaskSpans.set(key, result.span);

        // START_BLOCK_RESERVE_PLANNED_CAPACITY
        result.plannedIsoList.forEach((iso) => {
          addReservedHours(reservedByRole, roleId, iso, result.span.hoursByDate.get(iso) ?? 0);
        });
        // END_BLOCK_RESERVE_PLANNED_CAPACITY

        // START_BLOCK_ADVANCE_PLANNED_FLOOR
        if (result.plannedEndIso) {
          rolePlannedFloorIso.set(
            roleId,
            maxIso(rolePlannedFloorIso.get(roleId) ?? null, result.plannedEndIso),
          );
        }
        // END_BLOCK_ADVANCE_PLANNED_FLOOR

        stagePlannedEndIso = maxIso(
          stagePlannedEndIso,
          result.plannedEndIso ?? result.span.endIso,
        );
      });

      if (stagePlannedEndIso) {
        stagePrevPlannedEndIso = stagePlannedEndIso;
      }
    });
  });

  const mergedTaskSpans = new Map(planned.taskSpans);
  effectiveTaskSpans.forEach((span, key) => {
    mergedTaskSpans.set(key, span);
  });

  const featureSpans = new Map();
  scheduled.forEach((feature) => {
    let featureStartIso = null;
    let featureEndIso = null;
    feature.estimates.forEach((estimate) => {
      if (!(estimate.estimate > 0)) return;
      const span = mergedTaskSpans.get(`${feature.id}__${estimate.projectRoleId}`);
      if (!span) return;
      featureStartIso = minIso(featureStartIso, span.startIso);
      featureEndIso = maxIso(featureEndIso, span.endIso);
    });
    if (featureStartIso && featureEndIso) {
      featureSpans.set(feature.id, {
        startIso: featureStartIso,
        endIso: featureEndIso,
      });
    }
  });

  return {
    taskSpans: mergedTaskSpans,
    featureSpans,
  };
}
// END_METHOD_applyActualsToWorkloadSchedule
